<script setup lang="ts">
/**
 * "Prompt an edit" for a draft open in the phone DraftSheet: the creator says what to change and the composer
 * rewrites the draft in place (DraftViewer.revise, the inbox's own edit stream). A pill on the sheet's canvas, like
 * the chat dock: sparkle · field · mic, the mic turning into an arrow once there's text. While the field has focus
 * and nothing typed, a row of quick edits sits above it. Unsent text is kept per draft.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import DictationMicButton, { type DictationStatus } from '@/components/agent/DictationMicButton.vue'
import { readPromptDraft, writePromptDraft } from '@/composables/usePromptDraftCache'

const props = defineProps<{
    /** A rewrite is streaming in: the field locks and says so. */
    busy?: boolean
    disabled?: boolean
    /** Persist unsent text under this key. */
    draftKey?: string | null
    /** The draft is in the assistant's voice: offer "Write as me". */
    assistantVoice?: boolean
}>()
const emit = defineEmits<{ submit: [instruction: string] }>()

const QUICK_EDITS = ['Make it shorter', 'Warmer', 'More formal']
const MAX_HEIGHT = 112

const text = ref('')
const focused = ref(false)
const field = ref<HTMLTextAreaElement | null>(null)
const mic = ref<InstanceType<typeof DictationMicButton> | null>(null)
const dictation = ref<DictationStatus>('idle')
const dictationError = ref('')
let hydrating = false

const blocked = computed(() => Boolean(props.disabled || props.busy) || dictation.value !== 'idle')
const canSubmit = computed(() => !blocked.value && Boolean(text.value.trim()))
const chips = computed(() => (props.assistantVoice ? ['Write as me', ...QUICK_EDITS] : QUICK_EDITS))
const showChips = computed(() => focused.value && !text.value.trim() && !props.busy && dictation.value === 'idle')

watch(() => props.draftKey, (key, was) => {
    if (was && was !== key) writePromptDraft(was, text.value)
    hydrating = true
    text.value = readPromptDraft(key)
    void nextTick(() => { hydrating = false; resize() })
}, { immediate: true })
watch(text, (v) => {
    if (!hydrating) writePromptDraft(props.draftKey, v)
    void nextTick(resize)
})

function resize() {
    const el = field.value
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`
}

function send(instruction: string) {
    const v = instruction.trim()
    if (!v || props.disabled || props.busy) return
    emit('submit', v)
    // Put the keyboard away so the rewrite is in view.
    field.value?.blur()
}
function submit() {
    if (!canSubmit.value) return
    send(text.value)
}
function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return
    e.preventDefault()
    submit()
}
function onTranscript(val: string) {
    if (!val) return
    dictationError.value = ''
    text.value = (text.value ? `${text.value} ` : '') + val.trim()
    void nextTick(() => field.value?.focus())
}

onBeforeUnmount(() => {
    mic.value?.cancel()
    writePromptDraft(props.draftKey, text.value)
})

defineExpose({
    /** Clear the field (the edit landed). */
    clear() { text.value = '' },
})
</script>

<template>
    <div class="dpb" data-testid="draft-prompt-bar">
        <div class="dpb-chips" :class="{ 'is-open': showChips }" :aria-hidden="!showChips">
            <div class="dpb-chips-inner">
                <div class="dpb-chips-row">
                    <button
                        v-for="c in chips"
                        :key="c"
                        type="button"
                        tabindex="-1"
                        class="dpb-chip"
                        @pointerdown.prevent
                        @mousedown.prevent
                        @click="send(c)"
                    >{{ c }}</button>
                </div>
            </div>
        </div>

        <p v-if="dictationError" class="dpb-error" role="alert">
            <span class="min-w-0 flex-1 truncate">{{ dictationError }}</span>
            <button type="button" tabindex="-1" aria-label="Dismiss" @click="dictationError = ''">
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 5l6 6M11 5l-6 6" /></svg>
            </button>
        </p>

        <div class="dpb-field" :class="{ 'is-focused': focused, 'is-busy': busy, 'is-disabled': disabled && !busy }" @click="field?.focus()">
            <span class="dpb-spark" aria-hidden="true">
                <svg viewBox="0 0 16 16"><path d="M8 1.8l1.35 3.5a2 2 0 001.15 1.15L14 7.8l-3.5 1.35a2 2 0 00-1.15 1.15L8 13.8l-1.35-3.5a2 2 0 00-1.15-1.15L2 7.8l3.5-1.35A2 2 0 006.65 5.3L8 1.8z" /></svg>
            </span>
            <span v-if="busy" class="dpb-busy" role="status">Rewriting the draft…</span>
            <textarea
                v-show="!busy"
                ref="field"
                v-model="text"
                rows="1"
                enterkeyhint="send"
                autocomplete="off"
                autocapitalize="sentences"
                class="dpb-input"
                placeholder="Prompt an edit…"
                aria-label="Prompt an edit to the draft"
                :disabled="blocked && dictation === 'idle'"
                @keydown="onKeydown"
                @focus="focused = true"
                @blur="focused = false"
            />
            <span v-show="!text.trim() && !busy" class="dpb-mic">
                <DictationMicButton
                    ref="mic"
                    exclude-from-tab-order
                    picker-align="right"
                    icon="fa-waveform-lines"
                    :disabled="disabled || busy"
                    @transcript="onTranscript"
                    @error="(m) => (dictationError = m)"
                    @status="(s) => (dictation = s)"
                />
            </span>
            <button
                v-if="text.trim() || busy"
                type="button"
                tabindex="-1"
                class="dpb-send"
                :class="{ 'is-busy': busy }"
                :disabled="!canSubmit"
                aria-label="Rewrite the draft"
                data-testid="draft-prompt-send"
                @mousedown.prevent
                @click.stop="submit"
            >
                <span v-if="busy" class="dpb-spin" aria-hidden="true" />
                <svg v-else viewBox="0 0 16 16" aria-hidden="true"><path d="M8 12.5V3.8M4.2 7.4L8 3.6l3.8 3.8" /></svg>
            </button>
        </div>
    </div>
</template>

<style scoped>
.dpb { display: flex; flex-direction: column; padding: 8px 12px 10px; }

/* Quick edits fold open above the field (grid-rows 0fr → 1fr, so nothing jumps when they go). */
.dpb-chips {
    display: grid;
    grid-template-rows: 0fr;
    opacity: 0;
    transition: grid-template-rows 260ms var(--agent-ease), opacity 180ms var(--agent-ease);
}
.dpb-chips.is-open { grid-template-rows: 1fr; opacity: 1; }
.dpb-chips-inner { min-height: 0; overflow: hidden; }
.dpb-chips-row {
    display: flex;
    gap: 6px;
    padding: 0 2px 8px;
    overflow-x: auto;
    scrollbar-width: none;
}
.dpb-chips-row::-webkit-scrollbar { display: none; }
.dpb-chip {
    flex-shrink: 0;
    min-height: 34px;
    padding: 0 14px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    color: var(--agent-text-1);
    background: var(--agent-card);
    box-shadow: inset 0 0 0 1px var(--agent-card-border), 0 1px 2px rgb(15 15 15 / 0.05);
    -webkit-tap-highlight-color: transparent;
    transition: transform 120ms ease, background-color 120ms ease;
}
.dpb-chip:active { transform: scale(0.96); background: var(--agent-press); }

.dpb-error {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 4px 8px;
    font-size: 12.5px;
    color: #dc2626;
}
.dpb-error svg { width: 12px; height: 12px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; }

.dpb-field {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    min-height: 48px;
    padding: 6px 6px 6px 14px;
    border-radius: 24px;
    background: var(--agent-card);
    box-shadow: inset 0 0 0 1px var(--agent-card-border), var(--agent-card-shadow);
    cursor: text;
    transition: box-shadow 220ms var(--agent-ease);
}
.dpb-field.is-focused {
    box-shadow: inset 0 0 0 1px var(--agent-accent-ring), 0 0 0 4px var(--agent-accent-soft), var(--agent-card-shadow);
}
.dpb-field.is-disabled { opacity: 0.55; box-shadow: inset 0 0 0 1px var(--agent-card-border); }
.dpb-spark {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    height: 36px;
    margin-right: 4px;
}
.dpb-spark svg { width: 16px; height: 16px; fill: var(--agent-accent); }
.is-busy .dpb-spark svg { animation: dpb-twinkle 1.4s var(--agent-ease) infinite; }

.dpb-input {
    flex: 1;
    min-width: 0;
    max-height: 112px;
    padding: 7px 0;
    border: 0;
    outline: none;
    resize: none;
    background: transparent;
    font: inherit;
    font-size: 16px; /* 16px: iOS doesn't zoom the page on focus */
    line-height: 22px;
    color: var(--agent-text-1);
}
.dpb-input::placeholder { color: var(--agent-text-4); }
.dpb-input:focus { box-shadow: none; }

/* The rewrite in flight: the label shimmers like the agent's own progress line. */
.dpb-busy {
    flex: 1;
    min-width: 0;
    padding: 7px 0;
    font-size: 16px;
    line-height: 22px;
    color: transparent;
    background: linear-gradient(90deg, var(--agent-text-3) 0%, var(--agent-text-3) 35%, var(--agent-text-1) 50%, var(--agent-text-3) 65%, var(--agent-text-3) 100%);
    background-size: 220% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    animation: dpb-shimmer 1.6s linear infinite;
}

.dpb-mic { display: inline-flex; align-items: center; height: 36px; }
.dpb-send {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2) 0%, var(--agent-accent) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 4px 10px -4px rgb(251 76 31 / 0.55);
    -webkit-tap-highlight-color: transparent;
    animation: agent-pop 260ms var(--agent-spring) both;
    transition: transform 120ms ease;
}
.dpb-send:active:not(:disabled) { transform: scale(0.9); }
.dpb-send.is-busy { background: var(--agent-press); box-shadow: none; }
.dpb-send svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.dpb-spin {
    width: 15px;
    height: 15px;
    border-radius: 999px;
    border: 2px solid rgb(15 15 15 / 0.12);
    border-top-color: var(--agent-accent);
    animation: dpb-spin 0.7s linear infinite;
}
html.dark .dpb-spin { border-color: rgb(255 255 255 / 0.15); border-top-color: var(--agent-accent-2); }

@keyframes dpb-spin { to { transform: rotate(360deg); } }
@keyframes dpb-shimmer { from { background-position: 110% 0; } to { background-position: -110% 0; } }
@keyframes dpb-twinkle {
    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
    50% { transform: scale(0.82) rotate(18deg); opacity: 0.7; }
}
@media (prefers-reduced-motion: reduce) {
    .dpb-busy, .is-busy .dpb-spark svg, .dpb-spin, .dpb-send { animation: none !important; }
    .dpb-busy { color: var(--agent-text-3); background: none; }
}
</style>
